/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import isNull from 'lodash/isNull';
import { styled } from '@compiled/react';

import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import SectionMessage from '@atlaskit/section-message';

import { fg } from '@confluence/feature-gating';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';

import { fireScreenEvent, getActionName } from './AnalyticsEvents';
import {
	BULK_ARCHIVE_PAGE_SELECTION_LIMIT,
	ARCHIVE_PERMISSIONS_SUPPORT_LINK,
	UNARCHIVE_PERMISSIONS_SUPPORT_LINK,
} from './archivingConstants';
import type {
	ArchiveNestedPagesPermissionWarningQuery as ArchiveNestedPagesPermissionWarningQuery$data,
	ArchiveNestedPagesPermissionWarningQueryVariables as ArchiveNestedPagesPermissionWarningQuery$variables,
} from './__types__/ArchiveNestedPagesPermissionWarningQuery';

export const i18n = defineMessages({
	archiveContentLimitedPermissionWarningTitleV2: {
		id: 'archive-pages.content-nested.archive.limited-permission.title.V2',
		defaultMessage: `Some items can’t be archived`,
		description:
			'Title to a user when they have permission to archive only some, but not all of the nested content',
	},
	archiveContentLimitedPermissionWarningMessageV2: {
		id: 'archive-pages.content-nested.archive.limited-permission.message.V2',
		defaultMessage:
			'{count, plural, one {You don\'t have permission to archive # item. If you archive "{contentName}," that item will move up one level in the content tree.} other {You don\'t have permission to archive # items. If you archive "{contentName}," those items will move up one level in the content tree.}} <a>Why can\'t I archive nested items?</a>',
		description:
			'Conditional message to a user when they have permission to archive only some, but not all of the nested content. Shows count and content name dynamically.',
	},
	archiveContentSelectionLimitReachedWarningMessageV2: {
		id: 'archive-pages.content-nested.archive.selection-limit-reached.message.v2',
		defaultMessage:
			'Only 500 items can move at a time. Select a smaller branch to archive the parent and nested items together.',
		description:
			'Message to a user that they have selected more nested content to archive than allowed by our selection limit',
	},
	archiveContentCannotArchiveNestedItemsTitle: {
		id: 'archive-pages.content-nested.archive.cannot-archive-nested-items.title',
		defaultMessage: 'Nested items won’t be archived',
		description:
			"Title for when a user cannot archive any of the selected item's nested content because they either do not have permission to archive any of the nested content or they have selected more nested content to archive than allowed by our selection limit",
	},
	archiveContentCannotArchiveNestedItemsMessage: {
		id: 'archive-pages.content-nested.archive.cannot-archive-nested-items.message',
		defaultMessage:
			'You don’t have permission to archive all of the nested items. They’ll move up in the content tree. <a>Why can’t I archive nested items?</a>',
		description:
			"Message for when a user cannot archive any of the selected item's nested content because they either do not have permission to archive any of the nested content or they have selected more nested content to archive than allowed by our selection limit",
	},
	archivePermissionsInfoLinkAriaDescription: {
		id: 'archive-pages.dialog.archive.inherited-restrictions.info.link.aria-description',
		defaultMessage: 'archive permissions - link opens in a new tab',
		description:
			'Aria-label description of the Archive Permissions support link and what it will do when clicked.',
	},
	unarchiveContentNoPermissionWarningTitleV2: {
		id: 'archive-pages.content-nested.unarchive.cannot-restore-nested-items.title',
		defaultMessage: 'Nested items won’t be restored',
		description:
			'Title of message shown to a user when they do not have permissions to restore any of the nested items',
	},
	unarchiveContentNoPermissionWarningMessageV2: {
		id: 'archive-pages.content-nested.unarchive.cannot-restore-nested-items.message',
		defaultMessage:
			'You don’t have permission to restore all of the nested items. They’ll move up in the archive. <a>Why can’t I restore nested items?</a>',
		description:
			'Message to a user when they do not have permissions to restore any of the nested items',
	},
	unarchiveContentLimitedPermissionWarningTitleV2: {
		id: 'archive-pages.content-nested.unarchive.limited-permission.title.V2',
		defaultMessage: `{count, plural, one {1 nested item won’t be restored} other {{count} nested items won’t be restored}}`,
		description:
			'Title of message shown to a user when they do not have permissions to restore all of the nested items',
	},
	unarchiveContentLimitedPermissionWarningMessageV2: {
		id: 'archive-pages.content-nested.unarchive.limited-permission.message.V2',
		defaultMessage:
			'You don’t have permission to restore some nested items. They’ll move up in the archive. <a>Why can’t I restore nested items?</a>',
		description:
			'Message to a user when they do not have permissions to restore all of the nested items',
	},
	unarchiveContentSelectionLimitWarningTitleV2: {
		id: 'archive-pages.content-nested.unarchive.selection-limit-reached.title.V2',
		defaultMessage: 'Nested items won’t be restored',
		description:
			'Title of message shown to a user when they have selected too many items to restore',
	},
	unarchiveContentSelectionLimitWarningMessageV2: {
		id: 'archive-pages.content-nested.unarchive.selection-limit-reached.message.V2',
		defaultMessage:
			'Only 500 items can move at a time. Select a smaller branch to restore the parent and nested items together.',
		description:
			'Message to a user when they have when they have selected too many items to restore',
	},
	unarchivePermissionsWarningLinkAriaDescription: {
		id: 'archive-pages.content-nested.unarchive.limited-permission.message.link.aria-description',
		defaultMessage: 'restoring archived items - link opens in a new tab',
		description:
			'Aria-label description of the Restore archived items support link and what it will do when clicked.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionMessageWrapper = styled.div({
	marginTop: token('space.200'),
});

type ArchiveNestedPagesPermissionWarningProps = {
	contentId: string;
	isArchive: boolean;
	isSelectionLimitReached?: boolean;
};

// ArchiveNestedPagesPermissionWarning displays warnings for the following scenarios:
// 1) The user selected too many pages for archival/unarchival.
// 2) The user does not have permissions to archive/unarchive one or more of the selected nested pages.
export const ArchiveNestedPagesPermissionWarning: FC<ArchiveNestedPagesPermissionWarningProps> = ({
	contentId,
	isArchive,
	isSelectionLimitReached,
}) => {
	const firedScreenEventRef = useRef<boolean>(false);
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data, error } = useQuery<
		ArchiveNestedPagesPermissionWarningQuery$data,
		ArchiveNestedPagesPermissionWarningQuery$variables
	>(
		gql`
			query ArchiveNestedPagesPermissionWarningQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					title
					archivableDescendantsCount
					visibleDescendantsCount
				}
			}
		`,
		{
			variables: { contentId },
			fetchPolicy: 'cache-and-network',
		},
	);

	const viewPermissionsSupportLink = {
		a: (chunks: React.ReactFragment) =>
			fg('dst-a11y__replace-anchor-with-link__admin-experien') ? (
				<Link
					aria-label={intl.formatMessage(i18n.archivePermissionsInfoLinkAriaDescription)}
					target="_blank"
					href={ARCHIVE_PERMISSIONS_SUPPORT_LINK}
				>
					{chunks}
				</Link>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
				<a
					aria-label={intl.formatMessage(i18n.archivePermissionsInfoLinkAriaDescription)}
					target="_blank"
					href={ARCHIVE_PERMISSIONS_SUPPORT_LINK}
				>
					{chunks}
				</a>
			),
	};

	const viewRestoreItemsSupportLink = {
		a: (chunks: React.ReactFragment) =>
			fg('dst-a11y__replace-anchor-with-link__admin-experien') ? (
				<Link
					aria-label={intl.formatMessage(i18n.unarchivePermissionsWarningLinkAriaDescription)}
					target="_blank"
					href={UNARCHIVE_PERMISSIONS_SUPPORT_LINK}
				>
					{chunks}
				</Link>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
				<a
					aria-label={intl.formatMessage(i18n.unarchivePermissionsWarningLinkAriaDescription)}
					target="_blank"
					href={UNARCHIVE_PERMISSIONS_SUPPORT_LINK}
				>
					{chunks}
				</a>
			),
	};

	// Using "!data" instead of "loading" with "cache-and-network", as "load" can be true when cached
	// data is available and cause flickering due to reallocating subcomponents
	if (error) {
		return <ErrorDisplay error={error} />;
	} else if (!data) {
		return null;
	}

	const countArchivableNestedPages = data?.singleContent?.archivableDescendantsCount ?? null;
	const countNestedPages = data?.singleContent?.visibleDescendantsCount ?? null;
	const contentName = data?.singleContent?.title;
	const countNonArchivableNestedPages =
		Number(countNestedPages) - Number(countArchivableNestedPages);

	// return null if countArchivableNestedPages or countNestedPages is not a number or user is able to archive / restore all of nested pages
	if (
		(isNull(countArchivableNestedPages) ||
			isNull(countNestedPages) ||
			countArchivableNestedPages >= countNestedPages) &&
		!isSelectionLimitReached
	) {
		return null;
	}

	// At this point we are about to render some non-null content.  If we haven't fired the Screen
	// event, do so now.  Note that we can't use the standard "useEffect()"-based pattern to fire
	// an event on first render because the render is always being called even if it is not actually
	// showing anything on the page (that is, returning null).  We want the Screen event to be fired
	// the first time this component is actually visible.
	if (!firedScreenEventRef.current) {
		fireScreenEvent({
			createAnalyticsEvent,
			name: `${getActionName(isArchive)}NestedPagesPermissionWarning`,
		});
		firedScreenEventRef.current = true;
	}

	const archiveSelectionLimitReachedMessages = {
		mainTitle: i18n.archiveContentCannotArchiveNestedItemsTitle,
		mainMsg: i18n.archiveContentSelectionLimitReachedWarningMessageV2,
	};

	const archiveNoPermissionMessages = {
		mainTitle: i18n.archiveContentCannotArchiveNestedItemsTitle,
		mainMsg: i18n.archiveContentCannotArchiveNestedItemsMessage,
	};

	const archiveLimitedPermissionMessages = {
		mainTitle: i18n.archiveContentLimitedPermissionWarningTitleV2,
		mainMsg: i18n.archiveContentLimitedPermissionWarningMessageV2,
	};

	const unarchiveNoPermissionMessages = {
		mainTitle: i18n.unarchiveContentNoPermissionWarningTitleV2,
		mainMsg: i18n.unarchiveContentNoPermissionWarningMessageV2,
	};

	const unarchiveLimitedPermissionMessages = {
		mainTitle: i18n.unarchiveContentLimitedPermissionWarningTitleV2,
		mainMsg: i18n.unarchiveContentLimitedPermissionWarningMessageV2,
	};

	const unarchiveSelectionLimitReachedMessages = {
		mainTitle: i18n.unarchiveContentSelectionLimitWarningTitleV2,
		mainMsg: i18n.unarchiveContentSelectionLimitWarningMessageV2,
	};

	let i18nMessages;
	let count;
	if (isSelectionLimitReached) {
		i18nMessages = isArchive
			? archiveSelectionLimitReachedMessages
			: unarchiveSelectionLimitReachedMessages;
		count = BULK_ARCHIVE_PAGE_SELECTION_LIMIT;
	} else {
		// If we made it here, we know there is *at least one* nested page for which user lacks permission, out of
		// 1 or more total nested pages. We know this because the countArchivableNestedPages >= countNestedPages condition,
		// e.g. when user has permission for all nested pages, returns null earlier in the file.

		// So we could have....
		//    (a) one or more nested pages with no permission to archive ANY of them (that is, countArchivableNestedPages = 0
		//        and countNestedPages >= 1), or
		//    (b) multiple nested pages with some having permission and some not (that is, countArchivableNestedPages >= 1):
		//        We have at least 1 nested page where user HAS permission, and we already know at this point that the
		//        user also LACKS permission for at least 1 page, so we must have >=2 nested pages total and
		//        it is a mixed permissions scenario
		if (countArchivableNestedPages === 0) {
			// No permission to archive/restore any of the nested pages
			i18nMessages = isArchive ? archiveNoPermissionMessages : unarchiveNoPermissionMessages;
		} /* countArchivableNestedPages > 0 and countNestedPages >= 1 */ else {
			// Permission to archive/restore some - but not all - of the nested pages
			i18nMessages = isArchive
				? archiveLimitedPermissionMessages
				: unarchiveLimitedPermissionMessages;
		}
		count = countArchivableNestedPages;
	}

	const title = i18nMessages.mainTitle
		? {
				title: intl.formatMessage(i18nMessages.mainTitle, {
					count,
					countNonArchivableNestedPages,
				}),
			}
		: {};

	// Returns an updated support link if the user is restoring. If not, returns the existing support
	// link for archive permissions
	const supportLinkValue = !isArchive ? viewRestoreItemsSupportLink : viewPermissionsSupportLink;
	return (
		<>
			<SectionMessageWrapper>
				<SectionMessage appearance="warning" {...title}>
					{/* Note we can't put a test ID on a <FormattedMessage> component so we need a <div> to put it on instead */}
					<div data-testid="archive-pages-permissions-warning-message">
						<FormattedMessage
							{...i18nMessages.mainMsg}
							values={{
								count,
								contentName,
								...supportLinkValue,
							}}
						/>
					</div>
				</SectionMessage>
			</SectionMessageWrapper>
			{i18nMessages.infoMsg1 && i18nMessages.infoMsg2 ? (
				<SectionMessageWrapper>
					<SectionMessage appearance="information">
						<FormattedMessage {...i18nMessages.infoMsg1} />
						<br />
						<br />
						<FormattedMessage {...i18nMessages.infoMsg2} />
					</SectionMessage>
				</SectionMessageWrapper>
			) : null}
		</>
	);
};
